"use client";

import { useEffect, useState } from "react";
import { PlusOutlined } from "@ant-design/icons";

import Navbar from "../components/BrandsPartnerComponents/navbar";
import NavigationButton from "../components/BrandsPartnerComponents/navigation_button";
import CustomLoader from "../components/BrandsPartnerComponents/CustomLoader";
import IntroPartnerPage from "../homepage_partner/home";
import AboutYourBusinessPage from "../homepage_partner/about_your_business";
import GrowingTogetherPage from "../homepage_partner/growing_together";
import SuccessPartnerPage from "../homepage_partner/success_partner";
import PickMarketsPage from "./pick_markets";
import ExpansionEdgePage from "./expansion_edge";
import AddMarketModal from "../components/BrandsPartnerComponents/Modals/AddMarketsModal";

import {
  usePartnerOnboarding,
  PartnerOnboardingProvider,
} from "../../contexts/PartnerOnboardingContext";
import { createNewPartner } from "../../API/endpoints/Partner";
import {  PartnerType } from "./dtos/partner.dtos";
import { GetCountries } from "../../API/endpoints/Common";
import { NotificationPlacement } from "antd/es/notification/interface";
import { notification } from "antd";
import ServiceAndScopePage from "./service_and_scope copy";

// --- Separated Inner Component (must be inside provider) ---
const PartnerOnboardingContent = ({
  step,
  setStep,
  isMobile,
}: {
  step: number;
  setStep: (n: number) => void;
  isMobile: boolean;
}) => {
  interface OptionProps {
    id: string;
    name: string;
  }
  const { selectedMarkets, setSelectedMarkets } = usePartnerOnboarding();
  const [isAddMarketModalOpen, setAddMarketIsModalOpen] = useState(false);
  const [selectedToAdd, setSelectedToAdd] = useState<OptionProps[] | null>(null);
 

  const totalSteps = 6;
  const progressPercentage = ((step ) / totalSteps) * 100;
  const nextStep = () => setStep(step + 1);
  const previousStep = () => setStep(step - 1);

  const [data, setData] = useState<PartnerType>({
    name: "",
    country_of_origin: 0,
    poc_name: "",
    poc_designation: "",
    website: "",
    poc_email: "",
    poc_countrycode: "",
    poc_phone: "",
    markets: {},
    value_added_services: [],
    financial_assistance: [],
  });
  const [allMarkets, setAllMarkets] = useState<any[]>([]);
  useEffect(() => {
    (async () => {
      try {
        const response = await GetCountries();
        setAllMarkets(response.data.map((item: any) => item.name));
      } catch (error) {}
    })();
  }, []);

  const [api, contextHolder] = notification.useNotification();
  const openNotification = ( message:string, description:string) => {
    api.error({
      message: `${message}`,
      description:
        description,
      placement: "topRight",
    });
  };

  const handleCreateParter = async () => {
    createNewPartner(data)
      .then(async (response) => {
        nextStep();
      })
      .catch((err) => {
        const message = "Failed to create partner";
    const description =
      err?.response?.data?.message || // Axios-style error
      err?.message || // General JS error
      JSON.stringify(err); // Fallback to stringified error

    openNotification(message, description);
      });
  };

  const stepTitles = [
    "",
    "About Your Business",
    "Pick Markets",
    "Service & Scope",
    "Growing Together",
    "",
  ];

  return (
    <>{contextHolder}
      <Navbar
        progressPerc={progressPercentage}
        stepName={stepTitles[step - 1]}
        step={step - 1}
        totalSteps={totalSteps}
        button={
          step === 4 && isMobile ? (
            <div className="fixed right-0 top-2">
              <NavigationButton
                type="text"
                icon={<PlusOutlined />}
                onClick={() => setAddMarketIsModalOpen(true)}
                label="Add New"
              />
            </div>
          ) : null
        }
      />

      <div
        className={`w-full h-full md:min-h-screen bg-cover bg-no-repeat bg-center ${
          step !== 4 && !isMobile ? "common-padding" : "px-3 py-[100px]"
        }`}
        style={{
          backgroundImage: step !== 4 ? `url('/images/bg-ith-app.svg')` : "",
        }}
      >
        {step === 1 && <IntroPartnerPage />}
        {step === 2 && <AboutYourBusinessPage data={data} setData={setData} nextStep={nextStep} previousStep={previousStep} isMobile={isMobile} />}
        {step === 3 && (
          <PickMarketsPage previousStep={previousStep} nextStep={nextStep} data={data} setData={setData}/>
        )}
        {step === 4 &&
          (isMobile ? (
            <ExpansionEdgePage data={data} setData={setData}/>
          ) : (
            <ServiceAndScopePage
              nextStep={nextStep}
              previousStep={previousStep}
              data={data} setData={setData}
            />
          ))}
        {step === 5 && <GrowingTogetherPage data={data} setData={setData} previousStep={previousStep} nextStep={handleCreateParter} isMobile={isMobile} />}
        {step === 6 && <SuccessPartnerPage />}

        {/* Desktop Next/Previous Navigation */}
        {!isMobile && step ===1&& (
          <div
            className={`${
              step === 1 ? "" : "mt-[60px]"
            } inline-flex flex justify-start gap-x-[8px] w-full md:w-auto`}
          >
            {step !== 1 && (
              <NavigationButton
                onClick={previousStep}
                label="Previous"
                type="text"
              />
            )}
            <NavigationButton
              onClick={ nextStep}
              label={ "Next"}
              type="primary"
            />
          </div>
        )}
      </div>

      {/* Mobile Bottom Navigation */}
      {isMobile && step === 1 && (
        <div className="fixed bottom-0 left-0 right-0 flex justify-center px-4 py-4 gap-x-2 w-full shadow-lg z-20 bg-white">
          {step !== 1 && (
            <NavigationButton
              onClick={previousStep}
              label="Previous"
              type="text"
              block
            />
          )}
          <NavigationButton
            onClick={ nextStep}
            label={ "Next"}
            type="primary"
            block
          />
        </div>
      )}

      {/* Market Modal */}
      <AddMarketModal
        isOpen={isAddMarketModalOpen}
        setIsOpen={setAddMarketIsModalOpen}
        selectedToAdd={selectedToAdd}
        setSelectedToAdd={setSelectedToAdd}
        selectedMarkets={selectedMarkets}
        setSelectedMarkets={setSelectedMarkets}
        setData={setData}
        allMarkets={allMarkets}
      />
    </>
  );
};

// --- Wrapper Component with Provider and Loading ---
export default function PartnerOnboarding() {
  const [loading, setLoading] = useState(true);
  const [step, setStep] = useState(1);
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 768);
    handleResize();
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    const timer = setTimeout(() => setLoading(false), 1000);
    return () => clearTimeout(timer);
  }, []);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <CustomLoader />
      </div>
    );
  }

  return (
    <PartnerOnboardingProvider>
      <PartnerOnboardingContent
        step={step}
        setStep={setStep}
        isMobile={isMobile}
      />
    </PartnerOnboardingProvider>
  );
}
